import AdminShell from "@/components/AdminShell";
/**
 * KISSED University · Catalog Pricing & Enrollment Editor (super_admin).
 * Route: /admin/university/catalog
 *
 * Surfaces every program (division → level) with structured editors for:
 *   - Program Type (Certificate / Degree / Continuing Education)
 *   - Enrollment Configuration (application + manual review gating)
 *   - Tuition Configuration (Pay-In-Full / Pay-Per-Course pricing)
 *   - Academic Configuration (sequential progression, graduation, residency)
 *
 * Per-program changes are PATCHed individually so admins can stage edits
 * one program at a time. An "Advanced JSON" view is available below for
 * bulk reorganization of divisions/levels themselves.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft, Loader2, Save, BookOpen, GraduationCap, FileText, Stethoscope,
  Code, ChevronDown, ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TopNav } from "@/components/Layout";
import { adminAuth } from "@/lib/api";
import { universityApi } from "@/lib/universityApi";
import { formatApiError } from "@/lib/formatError";

const TYPE_OPTIONS = [
  { value: "certificate", label: "Certificate" },
  { value: "residency", label: "Residency" },
  { value: "degree", label: "Degree" },
  { value: "continuing_education", label: "Continuing Education" },
];
const TYPE_ICON = {
  certificate: BookOpen,
  residency: Stethoscope,
  degree: GraduationCap,
  continuing_education: FileText,
};

const DEFAULT_CFG = {
  requires_application: false,
  application_fee_required: false,
  application_fee_amount_usd: 0,
  requires_manual_review: false,
  allows_pay_in_full: true,
  allows_pay_per_course: true,
  allows_term_payment: false,
  sequential_course_progression: true,
  graduation_eligible: true,
  residency_required: false,
  dissertation_required: false,
};

export default function AdminUniversityCatalog() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState(null); // raw catalog doc
  const [updatedAt, setUpdatedAt] = useState("");
  const [updatedBy, setUpdatedBy] = useState("");
  const [openDivs, setOpenDivs] = useState({});
  const [showJson, setShowJson] = useState(false);
  const [jsonText, setJsonText] = useState("");
  const [savingJson, setSavingJson] = useState(false);

  useEffect(() => {
    if (!adminAuth.getToken()) { navigate("/admin/login"); return; }
    if (!adminAuth.isSuperAdmin()) {
      toast.error("Super admin access required.");
      navigate("/admin");
      return;
    }
    refresh();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await universityApi.adminGetCatalog();
      const { id, updated_at, updated_by, ...editable } = r.data || {};
      setCatalog(r.data);
      setJsonText(JSON.stringify(editable, null, 2));
      setUpdatedAt(updated_at || "");
      setUpdatedBy(updated_by || "");
      // open first division by default
      const firstKey = r.data?.divisions?.[0]?.key;
      if (firstKey) setOpenDivs((s) => ({ ...s, [firstKey]: true }));
    } catch (err) {
      toast.error(formatApiError(err, "Failed to load catalog"));
    } finally {
      setLoading(false);
    }
  };

  const saveJson = async () => {
    let parsed = null;
    try { parsed = JSON.parse(jsonText); }
    catch (e) { toast.error(`Invalid JSON: ${e.message}`); return; }
    setSavingJson(true);
    try {
      await universityApi.adminPutCatalog(parsed);
      toast.success("Catalog saved.");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err, "Save failed"));
    } finally {
      setSavingJson(false);
    }
  };

  const onProgramSaved = (divKey, levelKey, updatedProgram) => {
    setCatalog((cat) => {
      if (!cat) return cat;
      const next = { ...cat, divisions: cat.divisions.map((d) => {
        if (d.key !== divKey) return d;
        return { ...d, levels: d.levels.map((l) => {
          if (l.key !== levelKey) return l;
          return {
            ...l,
            program_type: updatedProgram.program_type,
            tuition_pay_in_full_usd: updatedProgram.tuition_pay_in_full_usd,
            per_course_price_usd: updatedProgram.per_course_price_usd,
            term_count: updatedProgram.term_count ?? 0,
            per_term_amount_usd: updatedProgram.per_term_amount_usd ?? 0,
            config: updatedProgram.config,
          };
        }) };
      })};
      // also refresh JSON view
      const { id, updated_at, updated_by, ...editable } = next || {};
      setJsonText(JSON.stringify(editable, null, 2));
      return next;
    });
  };

  const programCount = useMemo(
    () => (catalog?.divisions || []).reduce((acc, d) => acc + (d.levels?.length || 0), 0),
    [catalog]
  );

  return (
    <AdminShell door="university" title="Catalog">
      <TopNav
        right={
          <div className="flex items-center gap-2">
            <Link to="/admin/university/enrollments">
              <Button variant="ghost" className="rounded-full text-slate-700" data-testid="cat-link-enrollments">
                <GraduationCap className="h-4 w-4 mr-1" /> Enrollments
              </Button>
            </Link>
            <Link to="/admin/university">
              <Button variant="ghost" className="rounded-full text-slate-700" data-testid="back-to-queue">
                <ChevronLeft className="h-4 w-4 mr-1" /> Admissions queue
              </Button>
            </Link>
          </div>
        }
      />
      <div>
        <div className="kcis-overline text-purple-900">KISSED University</div>
        <h1 className="font-display text-3xl sm:text-4xl text-slate-900 mt-2 flex items-center gap-3">
          <BookOpen className="w-7 h-7 text-purple-900" /> Program Catalog
        </h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Every program&apos;s pricing and enrollment behavior is configured here. Changes are saved per
          program — the public landing, admissions form, and Enrollment page reflect changes immediately.
        </p>

        {loading ? (
          <div className="mt-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : !catalog ? null : (
          <>
            <div className="mt-4 text-xs text-slate-500" data-testid="cat-meta">
              {programCount} programs across {catalog.divisions?.length || 0} colleges
              {updatedAt && <> · last updated {new Date(updatedAt).toLocaleString()} · by {updatedBy || "—"}</>}
            </div>

            <div className="mt-6 space-y-4" data-testid="cat-divisions">
              {(catalog.divisions || []).map((div) => {
                const open = openDivs[div.key];
                return (
                  <div key={div.key} className="rounded-2xl border border-slate-200 bg-white">
                    <button
                      type="button"
                      onClick={() => setOpenDivs((s) => ({ ...s, [div.key]: !s[div.key] }))}
                      className="w-full text-left px-5 py-4 flex items-center gap-3"
                      data-testid={`cat-div-toggle-${div.key}`}
                    >
                      {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                      <div className="flex-1">
                        <div className="font-display text-lg text-slate-900">{div.name}</div>
                        <div className="text-xs text-slate-500">{div.levels?.length || 0} programs</div>
                      </div>
                    </button>
                    {open && (
                      <div className="border-t border-slate-100 divide-y divide-slate-100">
                        {(div.levels || []).map((lvl) => (
                          <ProgramEditor
                            key={lvl.key}
                            division={div}
                            level={lvl}
                            onSaved={(updatedProgram) => onProgramSaved(div.key, lvl.key, updatedProgram)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Advanced JSON editor for bulk catalog reorganization */}
            <details
              className="mt-10 rounded-2xl border border-slate-200 bg-white"
              onToggle={(e) => setShowJson(e.target.open)}
              data-testid="cat-json-advanced"
            >
              <summary className="px-5 py-4 cursor-pointer flex items-center gap-2 text-sm font-medium text-slate-700">
                <Code className="w-4 h-4 text-purple-900" /> Advanced · edit raw JSON
              </summary>
              {showJson && (
                <div className="px-5 pb-5 space-y-3">
                  <p className="text-xs text-slate-500">
                    Use this only when you need to add or remove divisions / levels. Per-program pricing &amp; config
                    are also editable above with friendlier forms.
                  </p>
                  <textarea
                    data-testid="catalog-json"
                    value={jsonText}
                    onChange={(e) => setJsonText(e.target.value)}
                    className="w-full h-[50vh] font-mono text-xs leading-relaxed border border-slate-200 rounded-xl p-4 bg-white"
                    spellCheck={false}
                  />
                  <div className="flex justify-end">
                    <Button onClick={saveJson} disabled={savingJson} className="bg-purple-900 hover:bg-purple-800" data-testid="catalog-save-btn">
                      {savingJson ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Save catalog JSON
                    </Button>
                  </div>
                </div>
              )}
            </details>
          </>
        )}
      </div>
    </AdminShell>
  );
}

function ProgramEditor({ division, level, onSaved }) {
  const initialCfg = useMemo(() => ({ ...DEFAULT_CFG, ...(level.config || {}) }), [level]);
  const [programType, setProgramType] = useState(level.program_type || "certificate");
  const [tuitionPif, setTuitionPif] = useState(level.tuition_pay_in_full_usd ?? 0);
  const [perCourse, setPerCourse] = useState(level.per_course_price_usd ?? 0);
  const [termCount, setTermCount] = useState(level.term_count ?? 0);
  const [perTerm, setPerTerm] = useState(level.per_term_amount_usd ?? 0);
  const [cfg, setCfg] = useState(initialCfg);
  const [saving, setSaving] = useState(false);

  const Icon = TYPE_ICON[programType] || BookOpen;
  const option = (level.label || "").toLowerCase().startsWith("level")
    ? `${level.label}: ${level.title}` : level.title;

  const dirty =
    programType !== (level.program_type || "certificate") ||
    Number(tuitionPif) !== Number(level.tuition_pay_in_full_usd ?? 0) ||
    Number(perCourse) !== Number(level.per_course_price_usd ?? 0) ||
    Number(termCount) !== Number(level.term_count ?? 0) ||
    Number(perTerm) !== Number(level.per_term_amount_usd ?? 0) ||
    JSON.stringify(cfg) !== JSON.stringify(initialCfg);

  const updateCfg = (patch) => setCfg((c) => ({ ...c, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        program_type: programType,
        tuition_pay_in_full_usd: parseFloat(tuitionPif) || 0,
        per_course_price_usd: parseFloat(perCourse) || 0,
        term_count: parseInt(termCount, 10) || 0,
        per_term_amount_usd: parseFloat(perTerm) || 0,
        config: {
          ...cfg,
          application_fee_amount_usd: parseFloat(cfg.application_fee_amount_usd) || 0,
        },
      };
      const r = await universityApi.adminPatchProgram(division.key, level.key, patch);
      toast.success("Program saved.");
      onSaved(r.data?.program || {
        program_type: programType,
        tuition_pay_in_full_usd: patch.tuition_pay_in_full_usd,
        per_course_price_usd: patch.per_course_price_usd,
        term_count: patch.term_count,
        per_term_amount_usd: patch.per_term_amount_usd,
        config: patch.config,
      });
    } catch (err) {
      toast.error(formatApiError(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-5 py-5" data-testid={`cat-program-${division.key}__${level.key}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-purple-50 text-purple-900 flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wide text-purple-900 font-medium">{level.label}</div>
          <div className="font-display text-base text-slate-900">{level.title}</div>
          <div className="text-xs text-slate-500 mt-0.5 font-mono">{option}</div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Program Information */}
        <section>
          <h4 className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Program Information</h4>
          <div className="mt-2 space-y-3">
            <div>
              <Label className="text-xs">Program Name</Label>
              <Input className="mt-1" value={option} disabled />
              <p className="text-[11px] text-slate-500 mt-1">Renamed in the Advanced JSON editor.</p>
            </div>
            <div>
              <Label className="text-xs">Program Type</Label>
              <select
                data-testid={`cat-${division.key}__${level.key}__type`}
                value={programType}
                onChange={(e) => setProgramType(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
              >
                {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
        </section>

        {/* Enrollment Configuration */}
        <section>
          <h4 className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Enrollment Configuration</h4>
          <div className="mt-2 space-y-2">
            <Switch
              testid={`cat-${division.key}__${level.key}__req-app`}
              label="Requires Application"
              value={cfg.requires_application}
              onChange={(v) => updateCfg({ requires_application: v })}
            />
            <Switch
              testid={`cat-${division.key}__${level.key}__fee-req`}
              label="Application Fee Required"
              value={cfg.application_fee_required}
              onChange={(v) => updateCfg({ application_fee_required: v })}
              disabled={!cfg.requires_application}
            />
            {cfg.application_fee_required && (
              <div className="pl-1">
                <Label className="text-xs">Application Fee Amount (USD)</Label>
                <Input
                  data-testid={`cat-${division.key}__${level.key}__fee-amount`}
                  className="mt-1"
                  type="number"
                  min="0"
                  step="1"
                  value={cfg.application_fee_amount_usd}
                  onChange={(e) => updateCfg({ application_fee_amount_usd: e.target.value })}
                />
              </div>
            )}
            <Switch
              testid={`cat-${division.key}__${level.key}__manual-review`}
              label="Requires Manual Review"
              value={cfg.requires_manual_review}
              onChange={(v) => updateCfg({ requires_manual_review: v })}
            />
          </div>
        </section>

        {/* Tuition Configuration */}
        <section>
          <h4 className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Tuition Configuration</h4>
          <div className="mt-2 space-y-3">
            <Switch
              testid={`cat-${division.key}__${level.key}__allow-pif`}
              label="Pay In Full Allowed"
              value={cfg.allows_pay_in_full}
              onChange={(v) => updateCfg({ allows_pay_in_full: v })}
            />
            {cfg.allows_pay_in_full && (
              <div className="pl-1">
                <Label className="text-xs">Tuition Pay In Full (USD)</Label>
                <Input
                  data-testid={`cat-${division.key}__${level.key}__pif`}
                  className="mt-1"
                  type="number"
                  min="0"
                  step="1"
                  value={tuitionPif}
                  onChange={(e) => setTuitionPif(e.target.value)}
                />
              </div>
            )}
            <Switch
              testid={`cat-${division.key}__${level.key}__allow-ppc`}
              label="Pay Per Course Allowed"
              value={cfg.allows_pay_per_course}
              onChange={(v) => updateCfg({ allows_pay_per_course: v })}
            />
            {cfg.allows_pay_per_course && (
              <div className="pl-1">
                <Label className="text-xs">Per Course Price (USD)</Label>
                <Input
                  data-testid={`cat-${division.key}__${level.key}__ppc`}
                  className="mt-1"
                  type="number"
                  min="0"
                  step="1"
                  value={perCourse}
                  onChange={(e) => setPerCourse(e.target.value)}
                />
              </div>
            )}
            <Switch
              testid={`cat-${division.key}__${level.key}__allow-term`}
              label="Term Payment Allowed"
              value={cfg.allows_term_payment}
              onChange={(v) => updateCfg({ allows_term_payment: v })}
            />
            {cfg.allows_term_payment && (
              <div className="pl-1 grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Number of Terms</Label>
                  <Input
                    data-testid={`cat-${division.key}__${level.key}__term-count`}
                    className="mt-1"
                    type="number"
                    min="0"
                    step="1"
                    value={termCount}
                    onChange={(e) => setTermCount(e.target.value)}
                  />
                </div>
                <div>
                  <Label className="text-xs">Per Term Amount (USD)</Label>
                  <Input
                    data-testid={`cat-${division.key}__${level.key}__per-term`}
                    className="mt-1"
                    type="number"
                    min="0"
                    step="1"
                    value={perTerm}
                    onChange={(e) => setPerTerm(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Academic Configuration */}
        <section>
          <h4 className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Academic Configuration</h4>
          <div className="mt-2 space-y-2">
            <Switch
              testid={`cat-${division.key}__${level.key}__sequential`}
              label="Sequential Course Progression"
              value={cfg.sequential_course_progression}
              onChange={(v) => updateCfg({ sequential_course_progression: v })}
            />
            <Switch
              testid={`cat-${division.key}__${level.key}__grad`}
              label="Graduation Eligible"
              value={cfg.graduation_eligible}
              onChange={(v) => updateCfg({ graduation_eligible: v })}
            />
            <Switch
              testid={`cat-${division.key}__${level.key}__residency`}
              label="Residency Required"
              value={cfg.residency_required}
              onChange={(v) => updateCfg({ residency_required: v })}
            />
            <Switch
              testid={`cat-${division.key}__${level.key}__dissertation`}
              label="Dissertation Required"
              value={cfg.dissertation_required}
              onChange={(v) => updateCfg({ dissertation_required: v })}
            />
          </div>
        </section>
      </div>

      <div className="mt-5 flex justify-end">
        <Button
          onClick={save}
          disabled={!dirty || saving}
          className="bg-purple-900 hover:bg-purple-800"
          data-testid={`cat-${division.key}__${level.key}__save`}
        >
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
          Save program
        </Button>
      </div>
    </div>
  );
}

function Switch({ label, value, onChange, disabled, testid }) {
  return (
    <label className={`flex items-center justify-between gap-3 text-sm ${disabled ? "opacity-50" : ""}`}>
      <span className="text-slate-700">{label}</span>
      <button
        type="button"
        data-testid={testid}
        disabled={disabled}
        onClick={() => onChange(!value)}
        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
          value ? "bg-purple-700" : "bg-slate-300"
        } ${disabled ? "cursor-not-allowed" : ""}`}
        aria-pressed={value}
      >
        <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-150 ${
          value ? "translate-x-5" : "translate-x-0"
        }`} />
      </button>
    </label>
  );
}
